import React from 'react';
import PropTypes from 'prop-types';
import { LoanService, ReturnService } from '../services';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContentText from "@material-ui/core/DialogContentText";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { centsToDollar, formatDate} from '../utils/formatters';
import { makeStyles } from '@material-ui/core/styles';
import { useNotification } from '../contexts/NotificationContext';
import { useError } from '../contexts/ErrorContext';
import red from '@material-ui/core/colors/red';
import blue from '@material-ui/core/colors/blue';
import LockIcon from '@material-ui/icons/Lock';
import Tooltip from '@material-ui/core/Tooltip';
import { isSuccessfulResponse } from '../utils/responseHelpers';

const styles = makeStyles(() => ({
  tableRow: {
      "&:hover": {
        backgroundColor: `${blue[50]} !important`,
        cursor: "pointer",
      }
  },
  tableRowBounced: {
    backgroundColor: red[200],
    "&:hover": {
      backgroundColor: `${red[200]} !important`,
      cursor: "pointer",
    }
  },
  lockedBouncedRow: {
    backgroundColor: red[200],
    "&:hover": {
      backgroundColor: `${red[200]} !important`
    }
  },
}));

const BounceMarkerDialog = (props) => {
  const classes = styles();
  const { createErrorNotification } = useNotification();
  const { showError } = useError();

  const getRowClass = function(historyItem, type) {
    if (type === "Return") {
      if (historyItem.status === 'bounced') {
        return classes.lockedBouncedRow;
      }
    }

    return historyItem.bounced ? classes.tableRowBounced : classes.tableRow;
  };

  const handlePaymentClick = async (transactionId, currentlyBounced, type) => {
    const isReturn = type === "Return";
    if (isReturn && currentlyBounced) {
      return;
    }

    const newBouncedValue = !currentlyBounced;
    const request = isReturn
      ? ReturnService.updateReturn(transactionId, { state_event: 'bounce' })
      : LoanService.updatePayment(transactionId, props.loanNumber, { bounced: newBouncedValue });

    request
      .then(res => {
        if (isSuccessfulResponse(res)) {
          props.onCancel();
          setTimeout(() => window.location.reload(), 100);
        } else {
          props.onCancel();
          createErrorNotification('Failed to mark bounce, please contact support');
        }
      })
      .catch(error => {
        showError(error, 'Failed to mark bounce, please contact support');
      });
  };

  return (
    <div>
        <Dialog
          open={props.showBounceDialog}
          onClose={props.onCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {props.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {props.message}
            </DialogContentText>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><b>Date</b></TableCell>
                    <TableCell><b>Type</b></TableCell>
                    <TableCell><b>Stage</b></TableCell>
                    <TableCell><b>Amount</b></TableCell>
                    <TableCell><b>Locked</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  { props.refunds
                    ? props.refunds
                        // .filter(historyItem => historyItem.type === "Payment" || historyItem.type === "Return")
                        .sort((a, b) => a.issuedAt > b.issuedAt ? 1 : -1)
                        .map((historyItem) => {
                          const isLocked = historyItem.bounced;
                          return (
                            <TableRow
                              hover
                              className={getRowClass(historyItem, 'Return')}
                              onClick={() => handlePaymentClick(historyItem.id, historyItem.bounced, 'Return')}
                              key={historyItem.id}
                            >
                              <TableCell>{formatDate(historyItem.issuedAt)}</TableCell>
                              <TableCell>Return</TableCell>
                              <TableCell>{historyItem.type === "Payment" ? historyItem.paymentStage : null}</TableCell>
                              <TableCell>{centsToDollar(historyItem.amountCents)}</TableCell>
                              <TableCell style={{ width: 20 }}>
                                {isLocked ? (
                                  <Tooltip title={'Bounced returns cannot be updated'}>
                                    <LockIcon />
                                  </Tooltip>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })
                    : null}

                    {
                      props.payments
                      ?
                        props.payments
                        .sort((a, b) => a.receivedAt > b.receivedAt ? 1 : -1)
                        .map((historyItem) => {
                          const isLocked = historyItem.bounced;
                          return (
                            <TableRow
                              hover
                              className={getRowClass(historyItem, 'Payment')}
                              onClick={() => handlePaymentClick(historyItem.id, historyItem.bounced, 'Payment')}
                              key={historyItem.id}
                            >
                              <TableCell>{formatDate(historyItem.receivedAt)}</TableCell>
                              <TableCell>Payment</TableCell>
                              <TableCell>{historyItem.stage}</TableCell>
                              <TableCell>{centsToDollar(historyItem.amountCents)}</TableCell>
                              <TableCell style={{ width: 20 }}>
                                {isLocked ? (
                                  <Tooltip title={'Bounced payments cannot be updated'}>
                                    <LockIcon />
                                  </Tooltip>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onCancel} color="primary">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
}

export default BounceMarkerDialog;

BounceMarkerDialog.propTypes = {
  showBounceDialog: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  loanNumber: PropTypes.string.isRequired,
  loanPaymentHistory: PropTypes.arrayOf(
    PropTypes.shape({
      transactionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      bounced: PropTypes.bool,
      status: PropTypes.string,
      date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      paymentStage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amount: PropTypes.number,
    })
  ),
};
